/**
 * Version-manifest + liveness REST client (protocol.md §11, web-client.md §7/§8). Mirrors the S0
 * backend's anonymous endpoints:
 *   GET /api/v1/version/manifest -> VersionManifest (protocolVersion window + per-client-type window)
 *   GET /time                    -> { serverTime }        (clock-skew probe)
 *   GET /health/live             -> { status, instance }  (reachability probe)
 *
 * All three are unauthenticated, so the diagnostics "connectivity half" works with no token. DOM-free.
 */
import type { HttpClient } from './http.ts';

/** How a client surface updates itself (VersionManifest.cs UpgradeCoordinates). */
export interface UpgradeCoordinates {
  /** Stable machine token: `reload` (web) | `pipx` (console). */
  method: string;
  /** Human one-liner the client shows. */
  instructions: string;
  command?: string | null;
  package?: string | null;
}

/** Per-client-type version window (VersionManifest.cs ClientVersionInfo). */
export interface ClientVersionInfo {
  /** Newest published version; a client below this SHOULD update (non-blocking). */
  latestVersion: string;
  /** Floor; a client below this is force-updated and locked out server-side. */
  minSupportedVersion: string;
  upgrade: UpgradeCoordinates;
}

/** The full manifest served at GET /api/v1/version/manifest (protocol.md §11). */
export interface VersionManifest {
  protocolVersion: number;
  minProtocolVersion: number;
  /** Keyed by client type (`web` | `console`); tolerant of unknown keys. */
  clients: Record<string, ClientVersionInfo>;
}

export interface ServerTime {
  serverTime: string;
}

export interface LivenessResult {
  status: string;
  instance?: string;
}

/** A single reachability probe result: latency in ms plus the parsed body. */
export interface ProbeResult<T> {
  latencyMs: number;
  body: T;
}

export class VersionClient {
  constructor(
    private readonly http: HttpClient,
    /** Injectable monotonic clock for latency measurement (defaults to Date.now). */
    private readonly now: () => number = () => Date.now(),
  ) {}

  manifest(signal?: AbortSignal): Promise<VersionManifest> {
    return this.http.get<VersionManifest>('/api/v1/version/manifest', { auth: false, signal });
  }

  /** GET /time, measuring round-trip latency (used by both reachability + clock-skew checks). */
  async time(signal?: AbortSignal): Promise<ProbeResult<ServerTime>> {
    const started = this.now();
    const body = await this.http.get<ServerTime>('/time', { auth: false, signal });
    return { latencyMs: this.now() - started, body };
  }

  /** GET /health/live, measuring round-trip latency. */
  async liveness(signal?: AbortSignal): Promise<ProbeResult<LivenessResult>> {
    const started = this.now();
    const body = await this.http.get<LivenessResult>('/health/live', { auth: false, signal });
    return { latencyMs: this.now() - started, body };
  }
}
